import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  Button,
  Chip,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { ArrowBack } from '@mui/icons-material';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalUrl from '../hooks/useCanonicalUrl';
import { getBlogPostBySlug, formatBlogDate } from '../utils/blogUtils';
import 'highlight.js/styles/github-dark.css';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState(slug ? getBlogPostBySlug(slug) : null);
  const canonicalUrl = useCanonicalUrl();

  usePageTitle(post ? `${post.title} | WaveScoop` : 'Loading...');

  useEffect(() => {
    if (slug) {
      const blogPost = getBlogPostBySlug(slug);
      setPost(blogPost);
    }
  }, [slug]);

  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!slug || !post) {
    return <Navigate to="/blog" replace />;
  }

  // Custom components for markdown rendering
  const components = {
    h1: ({ children, ...props }: any) => (
      <Typography variant="h2" component="h1" sx={{ mt: 5, mb: 3, fontWeight: 'bold', fontSize: { xs: '2rem', md: '2.5rem' } }} {...props}>
        {children}
      </Typography>
    ),
    h2: ({ children, ...props }: any) => (
      <Typography variant="h3" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '1.8rem' } }} {...props}>
        {children}
      </Typography>
    ),
    h3: ({ children, ...props }: any) => (
      <Typography variant="h4" component="h3" sx={{ mt: 3, mb: 2, fontWeight: 'bold', fontSize: { xs: '1.2rem', md: '1.4rem' } }} {...props}>
        {children}
      </Typography>
    ),
    p: ({ children, ...props }: any) => (
      <Typography paragraph sx={{ color: 'rgba(220, 220, 220, 0.9)', fontSize: '1.1rem', lineHeight: 1.8 }} {...props}>
        {children}
      </Typography>
    ),
    a: ({ href, children, ...props }: any) => {
      const isInternal = href?.startsWith('/');
      return isInternal ? (
        <MuiLink component={RouterLink} to={href} sx={{ color: 'secondary.light', '&:hover': { color: 'secondary.main' } }} {...props}>
          {children}
        </MuiLink>
      ) : (
        <MuiLink href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'secondary.light', '&:hover': { color: 'secondary.main' } }} {...props}>
          {children}
        </MuiLink>
      );
    },
    ul: ({ children, ...props }: any) => (
      <Box component="ul" sx={{ pl: 4, mb: 2, '& li': { color: 'rgba(220, 220, 220, 0.9)' } }} {...props}>
        {children}
      </Box>
    ),
    ol: ({ children, ...props }: any) => (
      <Box component="ol" sx={{ pl: 4, mb: 2, '& li': { color: 'rgba(220, 220, 220, 0.9)' } }} {...props}>
        {children}
      </Box>
    ),
    li: ({ children, ...props }: any) => (
      <Box component="li" sx={{ mb: 0.5 }} {...props}>
        {children}
      </Box>
    ),
    blockquote: ({ children, ...props }: any) => (
      <Typography
        component="blockquote"
        sx={{
          fontStyle: 'italic',
          color: 'rgba(210, 210, 210, 0.85)',
          borderLeft: 3,
          borderColor: 'rgba(255, 255, 255, 0.5)',
          pl: 2,
          my: 3,
          mx: 0,
          typography: 'h6',
        }}
        {...props}
      >
        {children}
      </Typography>
    ),
    strong: ({ children, ...props }: any) => (
      <Typography component="span" sx={{ fontWeight: 'bold' }} {...props}>
        {children}
      </Typography>
    ),
    em: ({ children, ...props }: any) => (
      <Typography component="span" sx={{ fontStyle: 'italic' }} {...props}>
        {children}
      </Typography>
    ),
    table: ({ children, ...props }: any) => (
      <TableContainer component={Paper} sx={{ my: 2, bgcolor: 'rgba(10, 30, 60, 0.6)', backdropFilter: 'blur(5px)', borderRadius: 1 }} {...props}>
        <Table size="small">{children}</Table>
      </TableContainer>
    ),
    thead: ({ children, ...props }: any) => <TableHead {...props}>{children}</TableHead>,
    tbody: ({ children, ...props }: any) => <TableBody {...props}>{children}</TableBody>,
    tr: ({ children, ...props }: any) => <TableRow {...props}>{children}</TableRow>,
    th: ({ children, ...props }: any) => (
      <TableCell sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.95)', borderColor: 'rgba(255, 255, 255, 0.2)' }} {...props}>
        {children}
      </TableCell>
    ),
    td: ({ children, ...props }: any) => (
      <TableCell sx={{ color: 'rgba(235, 235, 235, 0.9)', borderColor: 'rgba(255, 255, 255, 0.2)' }} {...props}>
        {children}
      </TableCell>
    ),
    hr: ({ ...props }: any) => <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.2)' }} {...props} />,
    img: ({ src, alt, ...props }: any) => {
      // Parse alt text for directives (e.g., "alt text|inline|width=800" or "alt text|inline|50%" or "alt text|medium")
      const parts = (alt || '').split('|').map((s: string) => s.trim());
      const actualAlt = parts[0];
      const directives = parts.slice(1);
      
      let width = '100%';
      let maxWidth = '800px'; // Default max width
      let isInline = false;
      
      // Process each directive
      directives.forEach((directive: string) => {
        if (directive === 'inline') {
          isInline = true;
        } else if (directive.includes('width=')) {
          // Custom pixel width: width=600
          const pixelWidth = directive.replace('width=', '');
          maxWidth = `${pixelWidth}px`;
          if (isInline) {
            width = 'auto'; // For inline images, use auto width with maxWidth constraint
          }
        } else if (directive.includes('%')) {
          // Percentage width: 50%
          width = directive;
          maxWidth = '100%';
        } else if (directive === 'small') {
          maxWidth = '400px';
          if (isInline) {
            width = 'auto';
          }
        } else if (directive === 'medium') {
          maxWidth = '600px';
          if (isInline) {
            width = 'auto';
          }
        } else if (directive === 'large') {
          maxWidth = '900px';
          if (isInline) {
            width = 'auto';
          }
        } else if (directive === 'full') {
          maxWidth = '100%';
        }
      });
      
      return (
        <Box
          component="img"
          src={src}
          alt={actualAlt || alt}
          loading="lazy"
          sx={{
            width,
            maxWidth,
            height: 'auto',
            display: isInline ? 'inline-block' : 'block',
            margin: isInline ? '1rem 0.5rem' : '2rem auto',
            verticalAlign: isInline ? 'middle' : undefined,
            borderRadius: 1,
            boxShadow: (theme) => theme.shadows[2],
            // Responsive sizing
            '@media (max-width: 600px)': {
              display: 'block', // Force block on mobile for better readability
              maxWidth: directives.some((d: string) => d.includes('%')) ? width : '100%',
              margin: '1rem auto',
            }
          }}
          {...props}
        />
      );
    },
    code: ({ inline, children, ...props }: any) => {
      return inline ? (
        <Box
          component="code"
          sx={{
            bgcolor: 'rgba(30, 30, 30, 0.8)',
            color: 'secondary.light',
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
            fontFamily: 'monospace',
            fontSize: '0.9em',
          }}
          {...props}
        >
          {children}
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{
            bgcolor: 'rgba(30, 30, 30, 0.8)',
            color: 'rgba(220, 220, 220, 0.9)',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            my: 2,
            '& code': {
              fontFamily: 'monospace',
              fontSize: '0.9em',
            },
          }}
        >
          <code {...props}>{children}</code>
        </Box>
      );
    },
  };

  return (
    <PublicPageLayout>
      <Helmet>
        <title>{post.title} | WaveScoop</title>
        <meta name="description" content={post.metaDescription || post.description} />
        <link rel="canonical" href={post.canonicalUrl || canonicalUrl} />
        {post.keywords && post.keywords.length > 0 && (
          <meta name="keywords" content={post.keywords.join(', ')} />
        )}
      </Helmet>

      <Container component="main" maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4, md: 6 },
            bgcolor: 'rgba(15, 51, 89, 0.70)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            color: 'rgba(240, 240, 240, 0.95)',
            borderRadius: 2,
          }}
        >
          {/* Back to Blog button */}
          <Button
            component={RouterLink}
            to="/blog"
            startIcon={<ArrowBack />}
            sx={{ mb: 3, color: 'rgba(220, 220, 220, 0.9)', '&:hover': { color: 'secondary.main' } }}
          >
            Back to Blog
          </Button>

          {/* Blog Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h1" component="h1" sx={{ mb: 2, fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' }, fontWeight: 'bold' }}>
              {post.title}
            </Typography>
            {post.description && (
              <Typography variant="h5" component="p" sx={{ color: 'rgba(220, 220, 220, 0.85)', mb: 3, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                {post.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'rgba(200, 200, 200, 0.8)' }}>
                {formatBlogDate(post.date)}
              </Typography>
              {post.author && (
                <Typography variant="body2" sx={{ color: 'rgba(200, 200, 200, 0.8)' }}>
                  By {post.author}
                </Typography>
              )}
              {post.readingTime && (
                <Typography variant="body2" sx={{ color: 'rgba(200, 200, 200, 0.8)' }}>
                  {post.readingTime}
                </Typography>
              )}
              <Chip label={post.category} size="small" sx={{ backgroundColor: 'secondary.dark', color: 'white' }} />
            </Box>
            {post.tags && post.tags.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {post.tags.map((tag, index) => (
                  <Chip key={index} label={tag} size="small" variant="outlined" sx={{ borderColor: 'rgba(255, 255, 255, 0.3)', color: 'rgba(220, 220, 220, 0.9)' }} />
                ))}
              </Box>
            )}
          </Box>

          <Divider sx={{ mb: 4, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

          {/* Blog Content */}
          <Box sx={{ '& > *:first-of-type': { mt: 0 } }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeHighlight]}
              components={components}
            >
              {post.content}
            </ReactMarkdown>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

          {/* CTA Section */}
          <Box
            sx={{
              textAlign: 'center',
              py: { xs: 4, md: 6 },
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              p: { xs: 2, md: 4 },
            }}
          >
            <Typography variant="h4" component="h2" sx={{ mb: 3, fontSize: { xs: '1.8rem', sm: '2.2rem' }, fontWeight: 'bold', color: 'white' }}>
              {post.cta?.title || 'Ready to predict your social media impact?'}
            </Typography>
            <Button 
              variant="contained" 
              color="secondary" 
              component={RouterLink} 
              to={post.cta?.buttonLink || '/login?source=blog'} 
              size="large" 
              sx={{ py: 1.5, px: 5, fontSize: '1.1rem' }}
            >
              {post.cta?.buttonText || 'Try WaveScoop Free →'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </PublicPageLayout>
  );
};

export default BlogPost;